// LIBRERIE/IMPORTS
import React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

// HOOKS
import { useGetAgentsByAgency } from "../../hooks/useGetAgents";
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";

// COMPONENTI
import SubHeaderDefault from "../Header/subHeader/SubHeaderDefault";
import AgentsSection from "./AgentsSection";
import ServerError from "../Error/ServerError";
import LoadingIndicator from "../Loaders/LoadingIndicator";
import WidgetContacts from "../Widgets/WidgetContacts/WidgetContacts";
import WidgetPartners from "../Widgets/WidgetPartners/WidgetPartners";

// CONFIGURAZIONI
import appConfig from "../../config/appConfig";

interface AboutUsProps {
    agencyId: number;
    showAgents: boolean;
}

const AboutUs: React.FC<AboutUsProps> = ({ agencyId, showAgents }) => {
    const { t, ready } = useTranslation();
    const { agents, loading: agentsLoading, error: agentsError } = useGetAgentsByAgency(agencyId);
    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(agencyId);

    const staticPartnerUrl = agencyId ? `http://www.casain24ore.it/fromsito.php?agReg=${agencyId}` : 'http://www.casain24ore.it';
    const staticPartner = { url: staticPartnerUrl, data: { imgSrc: '/assets/images/c24.png', imgAlt: 'Network Immobiliare' } };
    const rawDynamicPartnersData = appConfig.content.partners;
    const validDynamicPartnersData = rawDynamicPartnersData.filter(p => p && p.imgSrc && p.imgSrc.trim() !== '');
    const formattedDynamicPartners = validDynamicPartnersData.map(p => ({ url: p.url || '#', data: { imgSrc: p.imgSrc as string, imgAlt: p.imgAlt || 'Partner Logo' } }));
    const allPartnersForWidget = [staticPartner, ...formattedDynamicPartners];

    if (agencyLoading) return <LoadingIndicator />;
    if (agencyError) return <ServerError />;
    if (agentsLoading && showAgents) return <LoadingIndicator />;
    if (agentsError && showAgents) console.error("Error fetching agents:", agentsError);

    if (!ready) return null;

    return (
        <>
            <Helmet>
                <title>{t('aboutUsPage.pageTitle')} | {agency?.agencyName || 'Agenzia Immobiliare'}</title>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Lato:wght@300;400&display=swap" rel="stylesheet" />
            </Helmet>

            <SubHeaderDefault title={t('aboutUsPage.pageTitle')} isSeo={false} />

            <section className="space-ptb about-us">
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            <div className="row justify-content-center align-items-center">
                                <div className="col-12 col-sm-9 col-lg-6 col-xl-4 text-center">
                                    <img src="../assets/images/chi-siamo/entrata.jpg" alt={`${t('aboutUsPage.altImg1')}`} className="img-fluid mb-4" />
                                </div>
                                <div className="col-12 col-lg-6 col-xl-8">
                                    <p
                                        className="mb-4"
                                        dangerouslySetInnerHTML={{ __html: t('aboutUsPage.parag1') as string }}
                                    />
                                    <p className="mb-4">{t('aboutUsPage.parag2')}</p>
                                    <p className="mb-4"
                                        dangerouslySetInnerHTML={{ __html: t('aboutUsPage.parag3') as string }}
                                    />
                                </div>
                                <div className="col-12 col-lg-6 text-center">
                                    <img src="../assets/images/chi-siamo/squadra.jpg" alt={`${t('aboutUsPage.altImg2')}`} className="img-fluid mb-4" />
                                </div>
                                <div className="col-12 col-lg-6 text-center">
                                    <img src="../assets/images/chi-siamo/team.jpg" alt={`${t('aboutUsPage.altImg3')}`} className="img-fluid mb-4" />
                                </div>
                            </div>
                            {showAgents && agentsError && (
                                <div className="alert alert-danger">{t('aboutUsPage.agentsError', 'Errore nel caricamento degli agenti.')}</div>
                            )}
                            <AgentsSection
                                showAgents={showAgents}
                                agents={agents}
                                agentsLoading={agentsLoading}
                                agentsError={agentsError}
                            />
                        </div>
                        {/* Colonna Sidebar (Widgets) */}
                        <div className="col-12 col-lg-4">
                            <div className="sidebar">
                                <WidgetContacts
                                    agencyDataAddress={agency?.address_details?.address ?? ''}
                                    agencyDataCity={agency?.address_details?.city_name ?? ''}
                                    agencyDataComuneCapoluogo={agency?.address_details?.city_name === agency?.address_details?.province_name}
                                    agencyDataProvinciaSigla={agency?.address_details?.sigla ?? ''}
                                    agencyDataEmail={appConfig.agency.agencyEmail}
                                    agencyDataEmailShow={appConfig.ui.agencyWidget.showEmail}
                                    agencyDataPhone1={agency?.phone1 ?? ''}
                                    agencyDataPhone2={agency?.phone2 ?? ''}
                                    agencyDataInternationalPrefix={appConfig.agency.intlPrefix ?? '+39'}
                                    agencyDataMobile={agency?.mobile ?? ''}
                                    agencyDataMobile2={agency?.mobile2 ?? ''}
                                    agencyDataFax={agency?.fax ?? ''}
                                    agencySocialShow={appConfig.ui.agencyWidget.showSocialLinks}
                                    agencyDataWhatsapp={appConfig.socialLinks.whatsapp}
                                    agencyDataFacebook={appConfig.socialLinks.facebook}
                                    agencyDataInstagram={appConfig.socialLinks.instagram}
                                    agencyDataTwitter={appConfig.socialLinks.twitter}
                                    agencyDataLinkedin={appConfig.socialLinks.linkedin}
                                    agencyDataPinterest={appConfig.socialLinks.pinterest}
                                    agencyDataYoutube={appConfig.socialLinks.youtube}
                                />
                                <WidgetPartners partners={allPartnersForWidget} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};
export default AboutUs;